import { Dialog, Box, Text, TextLink, Stack, Table } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@/hooks/use-media-query'

type Props = {
  visible: boolean
  closeModal: () => void
}

const InfoDialog = ({ visible, closeModal }: Props) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery('(max-width: 997px)')

  const data = [
    {
      ratio: '0-30%',
      rating: t('l.standalone_trustscore.form.rent_to_income_table_low', 'Low'),
      description: t(
        'l.standalone_trustscore.form.rent_to_income_table_row1',
        'This is considered the ideal range as it means tenants are well within their budget and still have ample funds for other expenses.'
      )
    },
    {
      ratio: '31-50%',
      rating: t('l.standalone_trustscore.form.rent_to_income_table_average', 'Average'),
      description: t(
        'l.standalone_trustscore.form.rent_to_income_table_row2',
        'A rent-to-income ratio in this range is considered moderately high as although it is manageable, it can indicate that tenant may have trouble covering unexpected expenses.'
      )
    },
    {
      ratio: '51-100%',
      rating: t('l.standalone_trustscore.form.rent_to_income_table_high', 'High'),
      description: t(
        'l.standalone_trustscore.form.rent_to_income_table_row3',
        `Spending over 50% of one's monthly income on rent is considered financially risky as it leaves little flexibility for other living expenses and unexpected costs.`
      )
    }
  ]

  const columns = [
    {
      Header: t('l.standalone_trustscore.form.rent_to_income_title', 'Rent-to-Income Ratio'),
      accessor: 'ratio',
      gridWidth: { custom: '90px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return (
          <Box paddingY="small">
            <Text>{cell?.value}</Text>
          </Box>
        )
      }
    },
    {
      Header: t('l.application.view.trust_score_rating.title', 'Rating'),
      accessor: 'rating',
      gridWidth: { custom: '90px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return <Text>{cell?.value}</Text>
      }
    },
    {
      Header: t('l.application.view.description.label', 'Description'),
      accessor: 'description',
      gridWidth: { custom: '410px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return <Text>{cell?.value}</Text>
      }
    }
  ]

  const mobileColumns = [
    {
      Header: t('l.standalone_trustscore.form.rent_to_income_title', 'Rent-to-Income Ratio'),
      accessor: 'ratio',
      gridWidth: { custom: '90px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return (
          <Box paddingY="small">
            <Text>{cell?.value}</Text>
          </Box>
        )
      }
    },
    {
      Header: t('l.application.view.trust_score_rating.title', 'Rating'),
      accessor: 'rating',
      gridWidth: { custom: '220px' },
      Cell: (cell: { value: string; row: { original: any } }) => {
        return (
          <Box display="flex" flexDirection="column" gap="xsmall">
            <Text weight="strong">{cell?.value}</Text>
            <Text>{cell?.row?.original?.description}</Text>
          </Box>
        )
      }
    }
  ]

  return (
    <Dialog
      width="small"
      id="rent-to-income-Dialog"
      open={visible}
      onClose={closeModal}
      title={t('l.standalone_trustscore.form.rent_to_income_title', 'Rent-to-Income Ratio')}
      description={
        <Box paddingY="small">
          <Text>
            {t(
              'l.standalone_trustscore.form.rent_to_income_dialog_desc',
              'An applicant’s Rent-to-Income Ratio is calculated as a percentage based on their gross monthly income, divided by the amount of their rent payments. The recommended Rent-to-Income in Canada is 30%.'
            )}
          </Text>
        </Box>
      }
    >
      <Stack space="large">
        <Stack space="gutter">
          <Text size="large" weight="medium">
            {t(
              'l.standalone_trustscore.form.rent_to_income_dialog_title',
              'What is a good Rent-to-Income Ratio?'
            )}
          </Text>
          <Box>
            <Table data={data} columns={isMobile ? mobileColumns : columns} width="fit-content" />
          </Box>
        </Stack>
        <TextLink
          href="https://www.cmhc-schl.gc.ca/professionals/industry-innovation-and-leadership/industry-expertise/affordable-housing/about-affordable-housing/affordable-housing-in-canada"
          weight="regular"
          target="_blank"
          rel="noreferrer"
        >
          Learn more
        </TextLink>
      </Stack>
    </Dialog>
  )
}

export default InfoDialog
